// src/services/liveStreamService.ts
import { config } from '../config';
import { activeProfileHeader } from './viewerProfileService';
import unifiedAuth from '../utils/unifiedAuth';

const API_BASE_URL = config.apiBaseUrl;

export interface LiveStreamHost {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
}

export interface LiveStream {
  id: string;
  title: string;
  description?: string;
  slug: string;
  streamKey?: string;
  rtmpUrl?: string;
  playbackUrl?: string;
  thumbnailUrl?: string;
  recordingUrl?: string;
  /**
   * What `recordingUrl` is, stated by the server rather than inferred from the suffix.
   *
   * Replays go to the Bunny Stream library, so this is normally 'HLS'. Anything recorded
   * before that move is still 'PROGRESSIVE' and plays from the stored file.
   */
  replayDeliveryFormat?: 'HLS' | 'PROGRESSIVE';
  /** Null until somebody sets one, which the stream cannot be started without. */
  ageRating?: string | null;
  status: 'CREATED' | 'SCHEDULED' | 'CONNECTING' | 'LIVE' | 'PAUSED' | 'ENDED' | 'FAILED' | 'CANCELLED';
  host: LiveStreamHost;
  currentViewers: number;
  peakViewers: number;
  totalViews: number;
  likeCount: number;
  commentCount: number;
  scheduledStartTime?: string;
  actualStartTime?: string;
  endedAt?: string;
  createdAt: string;
  updatedAt: string;
  isRecordingEnabled: boolean;
  isChatEnabled: boolean;
  isReactionsEnabled: boolean;
  replayContentId?: string;
}

export interface LiveStreamChatMessage {
  id: string;
  streamId: string;
  userId?: string;
  username: string;
  userAvatar?: string;
  message: string;
  isPinned: boolean;
  isHighlighted: boolean;
  isFromHost: boolean;
  isFromModerator: boolean;
  status: 'VISIBLE' | 'HIDDEN' | 'DELETED';
  streamTimestampSeconds?: number;
  createdAt: string;
}

/** The ratings the backend accepts. Unrated is adults-only, so going live needs one. */
export const AGE_RATINGS = ['U', 'PG', '12', '15', '18'] as const;
export type AgeRatingValue = (typeof AGE_RATINGS)[number];

export interface CreateLiveStreamData {
  title: string;
  description?: string;
  scheduledStartTime?: string;
  /**
   * Required before the stream can be started.
   *
   * Optional here because a stream may be drafted before it is rated, but leaving it
   * unset means `/start` refuses with "An age rating is required before going live" -
   * unrated is treated as adults-only, so nobody could watch it.
   */
  ageRating?: AgeRatingValue;
}

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface LiveStreamAnalytics {
  totalStreams: number;
  currentlyLive: number;
  totalLiveViewers: number;
  totalPastStreams: number;
  totalStreamViews: number;
  totalStreamLikes: number;
  totalStreamComments: number;
  peakViewersRecord: number;
  streamsWithRecordings: number;
}

class LiveStreamService {
  private getAuthHeaders(includeContentType = true): Record<string, string> {
    // Authorization is attached by unifiedAuth.fetch, which refreshes on expiry.
    const headers: Record<string, string> = {};
    
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.data || data;
  }

  // ==================== Stream Discovery ====================

  /**
   * Get the current live stream with most viewers
   */
  async getCurrentLiveStream(): Promise<LiveStream | null> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/current`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (response.status === 404) {
        return null;
      }
      
      return this.handleResponse<LiveStream>(response);
    } catch (error) {
      console.warn('Error fetching current live stream:', error);
      return null;
    }
  }

  /**
   * Get all currently live streams
   */
  async getLiveStreams(page = 0, size = 20): Promise<PagedResponse<LiveStream>> {
    try {
      const response = await unifiedAuth.fetch(
        `${API_BASE_URL}/live-streams/live?page=${page}&size=${size}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );
      
      return this.handleResponse<PagedResponse<LiveStream>>(response);
    } catch (error) {
      console.error('Error fetching live streams:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size,
        number: page,
        first: true,
        last: true,
      };
    }
  }

  /**
   * Get upcoming scheduled streams
   */
  async getUpcomingStreams(page = 0, size = 20): Promise<PagedResponse<LiveStream>> {
    try {
      const response = await unifiedAuth.fetch(
        `${API_BASE_URL}/live-streams/upcoming?page=${page}&size=${size}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );
      
      return this.handleResponse<PagedResponse<LiveStream>>(response);
    } catch (error) {
      console.error('Error fetching upcoming streams:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size,
        number: page,
        first: true,
        last: true,
      };
    }
  }

  /**
   * Get past streams (replays)
   */
  async getPastStreams(page = 0, size = 20, recordingsOnly = false): Promise<PagedResponse<LiveStream>> {
    try {
      const response = await unifiedAuth.fetch(
        `${API_BASE_URL}/live-streams/past?page=${page}&size=${size}&recordingsOnly=${recordingsOnly}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );
      
      return this.handleResponse<PagedResponse<LiveStream>>(response);
    } catch (error) {
      console.error('Error fetching past streams:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size,
        number: page,
        first: true,
        last: true,
      };
    }
  }

  /**
   * Get popular past streams
   */
  async getPopularStreams(page = 0, size = 20): Promise<PagedResponse<LiveStream>> {
    try {
      const response = await unifiedAuth.fetch(
        `${API_BASE_URL}/live-streams/popular?page=${page}&size=${size}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );
      
      return this.handleResponse<PagedResponse<LiveStream>>(response);
    } catch (error) {
      console.error('Error fetching popular streams:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size,
        number: page,
        first: true,
        last: true,
      };
    }
  }

  // ==================== Single Stream Operations ====================

  /**
   * Get stream by ID
   */
  async getStreamById(id: string): Promise<LiveStream | null> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (response.status === 404) {
        return null;
      }
      
      return this.handleResponse<LiveStream>(response);
    } catch (error) {
      console.error('Error fetching stream:', error);
      return null;
    }
  }

  /**
   * Get stream by slug
   */
  async getStreamBySlug(slug: string): Promise<LiveStream | null> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/slug/${slug}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (response.status === 404) {
        return null;
      }
      
      return this.handleResponse<LiveStream>(response);
    } catch (error) {
      console.error('Error fetching stream:', error);
      return null;
    }
  }

  /**
   * Get stream statistics
   */
  async getStreamStats(streamId: string): Promise<Record<string, unknown>> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${streamId}/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      return this.handleResponse<Record<string, unknown>>(response);
    } catch (error) {
      console.error('Error fetching stream stats:', error);
      return {};
    }
  }

  // ==================== Stream Management (Host Only) ====================

  /**
   * Create a new live stream
   */
  async createStream(data: CreateLiveStreamData): Promise<LiveStream> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('Authentication required to create a stream');
    }

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<LiveStream>(response);
  }

  /**
   * Start a live stream
   */
  async startStream(streamId: string): Promise<LiveStream> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('Authentication required to start a stream');
    }

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${streamId}/start`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<LiveStream>(response);
  }

  /**
   * End a live stream
   */
  async endStream(streamId: string): Promise<LiveStream> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('Authentication required to end a stream');
    }

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${streamId}/end`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<LiveStream>(response);
  }

  /**
   * Upload a recording for a stream
   */
  async uploadRecording(streamId: string, recordingBlob: Blob): Promise<LiveStream> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('Authentication required to upload recording');
    }

    const formData = new FormData();
    formData.append('file', recordingBlob, `livestream-${streamId}-${Date.now()}.webm`);

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${streamId}/recording`, {
      method: 'POST',
      headers: {
        // Don't set Content-Type - let browser set it with boundary for multipart
      },
      body: formData,
    });

    return this.handleResponse<LiveStream>(response);
  }

  /**
   * Upload a thumbnail for a stream
   */
  async uploadThumbnail(streamId: string, thumbnailFile: File): Promise<LiveStream> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('Authentication required to upload thumbnail');
    }

    const formData = new FormData();
    formData.append('file', thumbnailFile);

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${streamId}/thumbnail`, {
      method: 'POST',
      headers: {
        // Don't set Content-Type - let browser set it with boundary for multipart
      },
      body: formData,
    });

    return this.handleResponse<LiveStream>(response);
  }

  /**
   * Update stream configuration
   */
  async updateStreamConfig(streamId: string, config: Partial<{
    title: string;
    description: string;
    isChatEnabled: boolean;
    isReactionsEnabled: boolean;
    isRecordingEnabled: boolean;
    /**
     * Settable after creation, which it was not. A stream created without a rating could
     * not be started and could not be rated either, so the only way out was a database
     * edit.
     */
    ageRating: AgeRatingValue;
  }>): Promise<LiveStream> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('Authentication required to update stream');
    }

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${streamId}/config`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(config),
    });

    return this.handleResponse<LiveStream>(response);
  }

  // ==================== Chat ====================

  /**
   * Get chat messages for a stream
   */
  async getChatMessages(streamId: string, page = 0, size = 50): Promise<PagedResponse<LiveStreamChatMessage>> {
    try {
      const response = await unifiedAuth.fetch(
        `${API_BASE_URL}/live-streams/${streamId}/chat?page=${page}&size=${size}`,
        {
          method: 'GET',
          // Which viewer on the account is reading. A profile capped below the account
          // cannot read the chat of a stream it cannot watch - but only if the server
          // is told which profile.
          headers: { ...this.getAuthHeaders(), ...activeProfileHeader() },
        }
      );
      
      return this.handleResponse<PagedResponse<LiveStreamChatMessage>>(response);
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size,
        number: page,
        first: true,
        last: true,
      };
    }
  }

  /**
   * Get latest chat messages
   */
  async getLatestChatMessages(streamId: string, limit = 50): Promise<LiveStreamChatMessage[]> {
    try {
      const response = await unifiedAuth.fetch(
        `${API_BASE_URL}/live-streams/${streamId}/chat/latest?limit=${limit}`,
        {
          method: 'GET',
          headers: { ...this.getAuthHeaders(), ...activeProfileHeader() },
        }
      );
      
      return this.handleResponse<LiveStreamChatMessage[]>(response);
    } catch (error) {
      console.error('Error fetching latest chat messages:', error);
      return [];
    }
  }

  /**
   * Send a chat message (REST fallback, prefer WebSocket)
   */
  async sendChatMessage(streamId: string, message: string): Promise<LiveStreamChatMessage> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('Authentication required to send messages');
    }

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${streamId}/chat`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), ...activeProfileHeader() },
      body: JSON.stringify({ message }),
    });

    return this.handleResponse<LiveStreamChatMessage>(response);
  }

  /**
   * Like a stream via REST API (for replays or when WebSocket is not connected)
   */
  async likeStream(streamId: string): Promise<LiveStream> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('Authentication required to like');
    }

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${streamId}/like`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<LiveStream>(response);
  }

  // ==================== Overall Stats ====================

  /**
   * Get overall live streaming statistics
   */
  async getOverallStats(): Promise<{
    liveStreamCount: number;
    totalLiveViewers: number;
    totalPastStreams: number;
  }> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/stats/overall`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      return this.handleResponse<{
        liveStreamCount: number;
        totalLiveViewers: number;
        totalPastStreams: number;
      }>(response);
    } catch (error) {
      console.error('Error fetching overall stats:', error);
      return {
        liveStreamCount: 0,
        totalLiveViewers: 0,
        totalPastStreams: 0,
      };
    }
  }

  // ==================== Admin Operations ====================

  /**
   * Delete a live stream (Admin only)
   * Can only delete streams that have ended or been cancelled
   */
  async deleteStream(streamId: string): Promise<void> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/${streamId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete stream');
    }
  }

  /**
   * Get live stream analytics for admin dashboard
   */
  async getLiveStreamAnalytics(): Promise<LiveStreamAnalytics> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-streams/analytics`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      return this.handleResponse<LiveStreamAnalytics>(response);
    } catch (error) {
      console.error('Error fetching live stream analytics:', error);
      return {
        totalStreams: 0,
        currentlyLive: 0,
        totalLiveViewers: 0,
        totalPastStreams: 0,
        totalStreamViews: 0,
        totalStreamLikes: 0,
        totalStreamComments: 0,
        peakViewersRecord: 0,
        streamsWithRecordings: 0,
      };
    }
  }

  // ==================== Utility Methods ====================

  /**
   * Check if a stream is currently live
   */
  isStreamLive(stream: LiveStream): boolean {
    return stream.status === 'LIVE';
  }

  /**
   * Get display-friendly status text
   */
  getStatusText(status: LiveStream['status']): string {
    const statusMap: Record<LiveStream['status'], string> = {
      CREATED: 'Created',
      SCHEDULED: 'Scheduled',
      CONNECTING: 'Connecting...',
      LIVE: '🔴 LIVE',
      PAUSED: 'Paused',
      ENDED: 'Ended',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
    };
    return statusMap[status] || status;
  }

  /**
   * Format viewer count for display
   */
  formatViewerCount(count: number): string {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  }

  /**
   * Calculate stream duration
   */
  getStreamDuration(stream: LiveStream): string {
    if (!stream.actualStartTime) return '--:--';
    
    const start = new Date(stream.actualStartTime);
    const end = stream.endedAt ? new Date(stream.endedAt) : new Date();
    const durationMs = end.getTime() - start.getTime();
    
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}

export const liveStreamService = new LiveStreamService();
export default liveStreamService;
